import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PRESTATIONS, toDisplay } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const KIND_META = {
  releve: { label: "Relevé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  estimation: { label: "Estimation", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  a_confirmer: { label: "À confirmer", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  suggestion: { label: "Suggestion", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

function KindBadge({ kind }) {
  const m = KIND_META[kind] || KIND_META.releve;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${m.cls}`}>{m.label}</span>;
}

function fieldLabel(prestation, key) {
  const def = PRESTATIONS[prestation];
  const f = def?.fields.find((x) => x.key === key);
  return f ? `${f.label}${f.unit ? ` (${f.unit})` : ""}` : key;
}
function dispVal(v) {
  return typeof v === "number" ? toDisplay(v) : String(v ?? "");
}

export function DictationDialog({ open, onOpenChange, prestation, existingLigne, onApply }) {
  const [phase, setPhase] = useState("input"); // input | proposal
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [proposal, setProposal] = useState(null);
  const [selected, setSelected] = useState({});
  const [applyZoneName, setApplyZoneName] = useState(true);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;

  useEffect(() => {
    if (open) {
      setPhase("input"); setTranscript(""); setProposal(null); setRecording(false);
      setBusy(false); setSelected({}); setApplyZoneName(true);
    } else {
      stopStream();
    }
    // eslint-disable-next-line
  }, [open]);

  const stopStream = () => {
    try { mediaRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Micro non disponible sur cet appareil"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec; rec.stream = stream; chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => { stopStream(); sendAudio(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" })); };
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Autorisation micro refusée. Utilisez la saisie de note écrite.");
    }
  };

  const stopRecording = () => { try { mediaRef.current?.stop(); } catch { /* noop */ } setRecording(false); };

  const sendAudio = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      const { data } = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript((prev) => (prev ? prev + " " : "") + (data.text || ""));
      if (!data.text) toast.warning("Transcription vide — parlez plus près du micro ou saisissez la note.");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const analyze = async () => {
    if (!transcript.trim()) { toast.error("Aucun texte à analyser"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/extract", {
        text: transcript, prestation: prestation || null, existing_ligne: existingLigne || {},
      });
      setProposal(data);
      const sel = {};
      Object.keys(data.ligne || {}).forEach((k) => { sel[k] = true; });
      setSelected(sel);
      setApplyZoneName(!!data.zone_name);
      setPhase("proposal");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const apply = () => {
    const ligne = {};
    Object.entries(proposal.ligne || {}).forEach(([k, v]) => { if (selected[k]) ligne[k] = v.value; });
    onApply({
      prestation: proposal.prestation,
      zone_name: applyZoneName ? proposal.zone_name : null,
      ligne,
      observations: proposal.observations || "",
    });
    onOpenChange(false);
  };

  const presLabel = prestation ? PRESTATIONS[prestation]?.name : (proposal?.prestation ? PRESTATIONS[proposal.prestation]?.name : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="dictation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icons.Mic className="h-5 w-5 text-emerald-600" />Dicter mon relevé</DialogTitle>
          <DialogDescription>
            L'assistant propose ; vous validez avant toute application. Aucune mesure n'est inventée.
          </DialogDescription>
        </DialogHeader>

        {!online && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
            <Icons.WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
            Les fonctions IA nécessitent une connexion. Hors ligne, saisissez le relevé manuellement.
          </div>
        )}

        {phase === "input" && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-5">
              {!recording ? (
                <Button onClick={startRecording} disabled={busy || !online} className="h-16 w-16 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="dictation-record-btn">
                  <Icons.Mic className="h-7 w-7" />
                </Button>
              ) : (
                <button onClick={stopRecording} className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-600 text-white ring-4 ring-rose-200 animate-pulse" data-testid="dictation-stop-btn">
                  <Icons.Square className="h-6 w-6" />
                </button>
              )}
              <span className="text-sm text-slate-500">
                {recording ? "Enregistrement en cours… parlez puis arrêtez" : busy ? "Transcription…" : "Appuyez pour dicter votre relevé"}
              </span>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Transcription (modifiable) ou note écrite collée</Label>
              <Textarea rows={4} value={transcript} onChange={(e) => setTranscript(e.target.value)}
                placeholder="Ex. Façade nord, environ 30 mètres de gouttières à 6 mètres de hauteur, beaucoup de feuilles, accès arrière à confirmer."
                data-testid="dictation-transcript" />
            </div>
          </div>
        )}

        {phase === "proposal" && proposal && (
          <div className="space-y-4" data-testid="dictation-proposal">
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Note analysée : </span>{transcript}
            </div>

            {presLabel && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Prestation :</span>
                <span className="font-medium text-slate-800">{presLabel}</span>
                {!prestation && <KindBadge kind="suggestion" />}
              </div>
            )}

            {proposal.zone_name && (
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5 text-sm">
                <input type="checkbox" checked={applyZoneName} onChange={(e) => setApplyZoneName(e.target.checked)}
                  className="h-4 w-4 accent-emerald-600" data-testid="dictation-apply-zonename" />
                <span className="text-slate-500">Nom de zone :</span>
                <span className="font-medium text-slate-800">{proposal.zone_name}</span>
                <KindBadge kind="suggestion" />
              </label>
            )}

            {Object.keys(proposal.ligne || {}).length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Champs proposés</p>
                {Object.entries(proposal.ligne).map(([k, v]) => {
                  const existing = existingLigne?.[k];
                  const overwrite = existing !== undefined && existing !== null && existing !== "";
                  return (
                    <label key={k} className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-2.5 text-sm" data-testid={`dictation-field-${k}`}>
                      <input type="checkbox" checked={!!selected[k]} onChange={(e) => setSelected((s) => ({ ...s, [k]: e.target.checked }))}
                        className="mt-1 h-4 w-4 accent-emerald-600" data-testid={`dictation-check-${k}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-700">{fieldLabel(proposal.prestation, k)}</span>
                          <KindBadge kind={v.kind} />
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-slate-600">
                          {overwrite && (
                            <>
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-400 line-through">{dispVal(existing)}</span>
                              <Icons.ArrowRight className="h-3 w-3 text-slate-400" />
                            </>
                          )}
                          <span className="font-mono-data font-semibold text-emerald-700">{dispVal(v.value)}</span>
                          {overwrite && <span className="text-[10px] text-amber-600">(remplace la valeur existante)</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Aucun champ chiffrable détecté dans cette note.</p>
            )}

            {proposal.observations && (
              <div className="rounded-lg border border-slate-200 p-2.5 text-sm">
                <p className="text-xs text-slate-500">Observations ajoutées</p>
                <p className="text-slate-700">{proposal.observations}</p>
              </div>
            )}

            {proposal.a_confirmer?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-sm">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-rose-700"><Icons.AlertTriangle className="h-4 w-4" />À confirmer</p>
                <ul className="list-inside list-disc text-rose-700">{proposal.a_confirmer.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {proposal.questions?.length > 0 && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-sm">
                <p className="mb-1 flex items-center gap-1.5 font-medium text-sky-700"><Icons.HelpCircle className="h-4 w-4" />Questions de l'assistant</p>
                <ul className="list-inside list-disc text-sky-700">{proposal.questions.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === "input" ? (
            <Button onClick={analyze} disabled={busy || !transcript.trim() || !online} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dictation-analyze-btn">
              {busy ? <Icons.Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icons.Sparkles className="mr-1.5 h-4 w-4" />}Analyser
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPhase("input")} data-testid="dictation-back-btn">Retour</Button>
              <Button onClick={apply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dictation-apply-btn">
                <Icons.Check className="mr-1.5 h-4 w-4" />Appliquer la sélection
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
